import type { Action, ActionDraft, ActiveAction } from '../../domain/action.ts'
import { amountForCompletion, rewardFor } from '../../domain/action.ts'
import { AREA_COLORS, type Area, type AreaDraft } from '../../domain/area.ts'
import type { CompletionResult, LeveleriaRepository } from '../../domain/leveleriaRepository'
import type { Specialization } from '../../domain/specialization'
import type { Objective, ObjectiveDraft, CheckpointAction } from '../../domain/objective.ts'
import { isDeadline, deadlineEnd } from '../../domain/objective.ts'
import type { ActivityRecord, Milestone } from '../../domain/history'
import type { RewardItem, RewardRedemption, RewardRedemptionResult } from '../../domain/reward'
import { LEVELERIA_BACKUP_FORMAT, LEVELERIA_BACKUP_SCHEMA, parseLeveleriaBackup, type LeveleriaBackup } from '../../domain/backup.ts'
import { APP_VERSION } from '../../domain/appVersion.ts'

const DATABASE_NAME = 'leveleria'
const DATABASE_VERSION = 1
const AREA_STORE = 'areas'
const ACTION_STORE = 'actions'
const SPECIALIZATION_STORE = 'specializations'
const OBJECTIVE_STORE = 'objectives'
const ACTIVITY_STORE = 'activityRecords'
const MILESTONE_STORE = 'milestones'
const REWARD_STORE = 'rewards'
const REDEMPTION_STORE = 'rewardRedemptions'
const STATE_STORE = 'appState'
const ACTIVE_ACTION_KEY = 'activeAction'
const COINS_KEY = 'coins'
const TARGET_REWARD_KEY = 'targetRewardId'
const ALL_STORES = [AREA_STORE, ACTION_STORE, SPECIALIZATION_STORE, OBJECTIVE_STORE, ACTIVITY_STORE, MILESTONE_STORE, REWARD_STORE, REDEMPTION_STORE, STATE_STORE] as const

interface StateRecord<T> { key: string; value: T }

const requestResult = <T>(request: IDBRequest<T>) => new Promise<T>((resolve, reject) => {
  request.addEventListener('success', () => resolve(request.result))
  request.addEventListener('error', () => reject(request.error ?? new Error('No se pudo completar la operación local.')))
})

const transactionDone = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.addEventListener('complete', () => resolve())
  transaction.addEventListener('abort', () => reject(transaction.error ?? new Error('La operación fue cancelada.')))
  transaction.addEventListener('error', () => reject(transaction.error ?? new Error('No se pudo guardar el cambio.')))
})

const openDatabase = () => new Promise<IDBDatabase>((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
  request.addEventListener('upgradeneeded', (event) => {
    const database = request.result
    if (!database.objectStoreNames.contains(AREA_STORE)) {
      const store = database.createObjectStore(AREA_STORE, { keyPath: 'id' })
      store.createIndex('createdAt', 'createdAt')
    }
    if (!database.objectStoreNames.contains(ACTION_STORE)) {
      const store = database.createObjectStore(ACTION_STORE, { keyPath: 'id' })
      store.createIndex('areaId', 'areaId')
      store.createIndex('createdAt', 'createdAt')
    }
    if (!database.objectStoreNames.contains(SPECIALIZATION_STORE)) {
      const store = database.createObjectStore(SPECIALIZATION_STORE, { keyPath: 'id' })
      store.createIndex('areaId', 'areaId')
      store.createIndex('createdAt', 'createdAt')
    }
    if (!database.objectStoreNames.contains(OBJECTIVE_STORE)) {
      const store = database.createObjectStore(OBJECTIVE_STORE, { keyPath: 'id' })
      store.createIndex('areaId', 'areaId')
      store.createIndex('createdAt', 'createdAt')
    }
    let milestoneStore: IDBObjectStore
    if (!database.objectStoreNames.contains(ACTIVITY_STORE)) {
      const store = database.createObjectStore(ACTIVITY_STORE, { keyPath: 'id' })
      store.createIndex('areaId', 'areaId')
      store.createIndex('completedAt', 'completedAt')
    }
    if (!database.objectStoreNames.contains(MILESTONE_STORE)) {
      milestoneStore = database.createObjectStore(MILESTONE_STORE, { keyPath: 'id' })
      milestoneStore.createIndex('areaId', 'areaId')
      milestoneStore.createIndex('achievedAt', 'achievedAt')
    } else milestoneStore = request.transaction!.objectStore(MILESTONE_STORE)
    if (!database.objectStoreNames.contains(REWARD_STORE)) {
      const store = database.createObjectStore(REWARD_STORE, { keyPath: 'id' })
      store.createIndex('createdAt', 'createdAt')
    }
    if (!database.objectStoreNames.contains(REDEMPTION_STORE)) {
      const store = database.createObjectStore(REDEMPTION_STORE, { keyPath: 'id' })
      store.createIndex('rewardId', 'rewardId')
      store.createIndex('redeemedAt', 'redeemedAt')
    }
    if (!database.objectStoreNames.contains(STATE_STORE)) database.createObjectStore(STATE_STORE, { keyPath: 'key' })
    if (event.oldVersion > 0 && event.oldVersion < 5) {
      const transaction = request.transaction!
      const objectiveStore = transaction.objectStore(OBJECTIVE_STORE)
      const areaStore = transaction.objectStore(AREA_STORE)
      objectiveStore.openCursor().addEventListener('success', (cursorEvent) => {
        const cursor = (cursorEvent.target as IDBRequest<IDBCursorWithValue | null>).result
        if (!cursor) return
        const objective = cursor.value as Objective
        if (objective.status === 'completed') {
          const areaRequest = areaStore.get(objective.areaId) as IDBRequest<Area | undefined>
          areaRequest.addEventListener('success', () => {
            const area = areaRequest.result
            if (area) milestoneStore.put({ id: `objective:${objective.id}`, areaId: area.id, areaName: area.name, title: objective.name, kind: 'objective', xp: 0, coins: 0, achievedAt: objective.updatedAt } satisfies Milestone)
          })
        }
        cursor.continue()
      })
    }
    if (event.oldVersion > 0 && event.oldVersion < 7) {
      const rewardStore = request.transaction!.objectStore(REWARD_STORE)
      rewardStore.openCursor().addEventListener('success', (cursorEvent) => {
        const cursor = (cursorEvent.target as IDBRequest<IDBCursorWithValue | null>).result
        if (!cursor) return
        const reward = cursor.value as RewardItem
        if (typeof reward.updatedAt !== 'number') cursor.update({ ...reward, updatedAt: reward.createdAt })
        cursor.continue()
      })
    }
  })
  request.addEventListener('success', () => resolve(request.result))
  request.addEventListener('error', () => reject(request.error ?? new Error('No se pudo abrir el almacenamiento local.')))
  request.addEventListener('blocked', () => reject(new Error('Cierra otras pestañas de Leveleria e inténtalo de nuevo.')))
})

const clampTotal = (current: number, earned: number) => Math.min(2_147_483_647, Math.max(0, current) + Math.max(0, earned))
const normalizedAction = (action: Action): Action => ({
  ...action,
  categoryIds: Array.isArray(action.categoryIds) ? action.categoryIds : [],
  objectiveIds: Array.isArray(action.objectiveIds) ? action.objectiveIds : [],
})
const normalizedReward = (reward: RewardItem): RewardItem => ({
  ...reward,
  updatedAt: typeof reward.updatedAt === 'number' ? reward.updatedAt : reward.createdAt,
})
const checkedName = (value: string, maxLength: number, label: string) => {
  const name = value.trim().slice(0, maxLength)
  if (!name) throw new Error(`Escribe un nombre para ${label}.`)
  return name
}
const checkedRewardValue = (value: number, minimum: number, label: string) => {
  const result = Math.trunc(value)
  if (!Number.isSafeInteger(result) || result < minimum || result > 9_999_999) throw new Error(`${label} debe ser de ${minimum} a 9999999.`)
  return result
}

export class IndexedDbLeveleriaRepository implements LeveleriaRepository {
  async exportBackup(): Promise<LeveleriaBackup> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([...ALL_STORES], 'readonly')
      const [areas, actions, specializations, objectives, activityRecords, milestones, rewards, rewardRedemptions, state] = await Promise.all([
        requestResult(transaction.objectStore(AREA_STORE).getAll() as IDBRequest<Area[]>),
        requestResult(transaction.objectStore(ACTION_STORE).getAll() as IDBRequest<Action[]>),
        requestResult(transaction.objectStore(SPECIALIZATION_STORE).getAll() as IDBRequest<Specialization[]>),
        requestResult(transaction.objectStore(OBJECTIVE_STORE).getAll() as IDBRequest<Objective[]>),
        requestResult(transaction.objectStore(ACTIVITY_STORE).getAll() as IDBRequest<ActivityRecord[]>),
        requestResult(transaction.objectStore(MILESTONE_STORE).getAll() as IDBRequest<Milestone[]>),
        requestResult(transaction.objectStore(REWARD_STORE).getAll() as IDBRequest<RewardItem[]>),
        requestResult(transaction.objectStore(REDEMPTION_STORE).getAll() as IDBRequest<RewardRedemption[]>),
        requestResult(transaction.objectStore(STATE_STORE).getAll() as IDBRequest<Array<StateRecord<unknown>>>),
      ])
      await transactionDone(transaction)
      const stateValue = <T,>(key: string) => state.find((item) => item.key === key)?.value as T | undefined
      return parseLeveleriaBackup({
        format: LEVELERIA_BACKUP_FORMAT,
        schemaVersion: LEVELERIA_BACKUP_SCHEMA,
        appVersion: APP_VERSION,
        exportedAt: new Date().toISOString(),
        data: {
          areas,
          actions: actions.map(normalizedAction),
          specializations,
          objectives,
          activityRecords: activityRecords.map((record) => ({ ...record, startedAt: typeof record.startedAt === 'number' ? record.startedAt : null })),
          milestones,
          rewards: rewards.map(normalizedReward),
          rewardRedemptions,
          activeAction: stateValue<ActiveAction>(ACTIVE_ACTION_KEY) ?? null,
          coins: Math.max(0, stateValue<number>(COINS_KEY) ?? 0),
          targetRewardId: stateValue<string>(TARGET_REWARD_KEY) ?? null,
        },
      })
    } finally { database.close() }
  }

  async importBackup(candidate: LeveleriaBackup): Promise<void> {
    const backup = parseLeveleriaBackup(candidate)
    const database = await openDatabase()
    try {
      const transaction = database.transaction([...ALL_STORES], 'readwrite')
      ALL_STORES.forEach((storeName) => transaction.objectStore(storeName).clear())
      backup.data.areas.forEach((item) => transaction.objectStore(AREA_STORE).put(item))
      backup.data.actions.forEach((item) => transaction.objectStore(ACTION_STORE).put(item))
      backup.data.specializations.forEach((item) => transaction.objectStore(SPECIALIZATION_STORE).put(item))
      backup.data.objectives.forEach((item) => transaction.objectStore(OBJECTIVE_STORE).put(item))
      backup.data.activityRecords.forEach((item) => transaction.objectStore(ACTIVITY_STORE).put(item))
      backup.data.milestones.forEach((item) => transaction.objectStore(MILESTONE_STORE).put(item))
      backup.data.rewards.forEach((item) => transaction.objectStore(REWARD_STORE).put(item))
      backup.data.rewardRedemptions.forEach((item) => transaction.objectStore(REDEMPTION_STORE).put(item))
      const stateStore = transaction.objectStore(STATE_STORE)
      stateStore.put({ key: COINS_KEY, value: backup.data.coins } satisfies StateRecord<number>)
      if (backup.data.activeAction) stateStore.put({ key: ACTIVE_ACTION_KEY, value: backup.data.activeAction } satisfies StateRecord<ActiveAction>)
      if (backup.data.targetRewardId) stateStore.put({ key: TARGET_REWARD_KEY, value: backup.data.targetRewardId } satisfies StateRecord<string>)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async list(): Promise<Area[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(AREA_STORE, 'readonly')
      const areas = await requestResult(transaction.objectStore(AREA_STORE).getAll() as IDBRequest<Area[]>)
      await transactionDone(transaction)
      return areas.sort((a, b) => a.createdAt - b.createdAt)
    } finally { database.close() }
  }

  async create(draft: AreaDraft): Promise<Area> {
    const name = checkedName(draft.name, 60, 'el Área')
    if (!AREA_COLORS.includes(draft.color)) throw new Error('El color del Área no es válido.')
    const timestamp = Date.now()
    const area: Area = { id: crypto.randomUUID(), name, color: draft.color, xp: 0, activeObjectiveName: null, createdAt: timestamp, updatedAt: timestamp }
    const database = await openDatabase()
    try {
      const transaction = database.transaction(AREA_STORE, 'readwrite')
      transaction.objectStore(AREA_STORE).add(area)
      await transactionDone(transaction)
      return area
    } finally { database.close() }
  }

  async update(id: string, draft: AreaDraft): Promise<Area> {
    const name = checkedName(draft.name, 60, 'el Área')
    if (!AREA_COLORS.includes(draft.color)) throw new Error('El color del Área no es válido.')
    const database = await openDatabase()
    try {
      const transaction = database.transaction(AREA_STORE, 'readwrite')
      const store = transaction.objectStore(AREA_STORE)
      const current = await requestResult(store.get(id) as IDBRequest<Area | undefined>)
      if (!current) throw new Error('El Área ya no existe.')
      const updated = { ...current, name, color: draft.color, updatedAt: Date.now() }
      store.put(updated)
      await transactionDone(transaction)
      return updated
    } finally { database.close() }
  }

  async delete(id: string): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, ACTION_STORE, SPECIALIZATION_STORE, OBJECTIVE_STORE, STATE_STORE], 'readwrite')
      const actionStore = transaction.objectStore(ACTION_STORE)
      const specializationStore = transaction.objectStore(SPECIALIZATION_STORE)
      const objectiveStore = transaction.objectStore(OBJECTIVE_STORE)
      const stateStore = transaction.objectStore(STATE_STORE)
      const [actionKeys, specializationKeys, objectiveKeys, activeRecord] = await Promise.all([
        requestResult(actionStore.index('areaId').getAllKeys(IDBKeyRange.only(id))),
        requestResult(specializationStore.index('areaId').getAllKeys(IDBKeyRange.only(id))),
        requestResult(objectiveStore.index('areaId').getAllKeys(IDBKeyRange.only(id))),
        requestResult(stateStore.get(ACTIVE_ACTION_KEY) as IDBRequest<StateRecord<ActiveAction> | undefined>),
      ])
      transaction.objectStore(AREA_STORE).delete(id)
      actionKeys.forEach((key) => actionStore.delete(key))
      specializationKeys.forEach((key) => specializationStore.delete(key))
      objectiveKeys.forEach((key) => objectiveStore.delete(key))
      if (activeRecord?.value.areaId === id) stateStore.delete(ACTIVE_ACTION_KEY)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async listSpecializations(areaId?: string): Promise<Specialization[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(SPECIALIZATION_STORE, 'readonly')
      const store = transaction.objectStore(SPECIALIZATION_STORE)
      const request = areaId ? store.index('areaId').getAll(IDBKeyRange.only(areaId)) : store.getAll()
      const items = await requestResult(request as IDBRequest<Specialization[]>)
      await transactionDone(transaction)
      return items.sort((a, b) => a.createdAt - b.createdAt)
    } finally { database.close() }
  }

  async createSpecialization(areaId: string, proposedName: string): Promise<Specialization> {
    const name = proposedName.trim().slice(0, 30)
    if (!name) throw new Error('Escribe un nombre para la Especialización.')
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, SPECIALIZATION_STORE], 'readwrite')
      const store = transaction.objectStore(SPECIALIZATION_STORE)
      const [area, existing] = await Promise.all([
        requestResult(transaction.objectStore(AREA_STORE).get(areaId) as IDBRequest<Area | undefined>),
        requestResult(store.index('areaId').getAll(IDBKeyRange.only(areaId)) as IDBRequest<Specialization[]>),
      ])
      if (!area) throw new Error('El Área ya no existe.')
      if (existing.some((item) => item.name.toLocaleLowerCase() === name.toLocaleLowerCase())) throw new Error('Ya existe una Especialización con ese nombre.')
      const item: Specialization = { id: crypto.randomUUID(), areaId, name, xp: 0, createdAt: Date.now() }
      store.add(item)
      await transactionDone(transaction)
      return item
    } finally { database.close() }
  }

  async deleteSpecialization(id: string): Promise<Action[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([SPECIALIZATION_STORE, ACTION_STORE], 'readwrite')
      const specializationStore = transaction.objectStore(SPECIALIZATION_STORE)
      const actionStore = transaction.objectStore(ACTION_STORE)
      const category = await requestResult(specializationStore.get(id) as IDBRequest<Specialization | undefined>)
      if (!category) throw new Error('La Especialización ya no existe.')
      const actions = await requestResult(actionStore.index('areaId').getAll(IDBKeyRange.only(category.areaId)) as IDBRequest<Action[]>)
      const updated = actions.map(normalizedAction).map((action) => ({ ...action, categoryIds: action.categoryIds.filter((categoryId) => categoryId !== id), updatedAt: Date.now() }))
      updated.forEach((action) => actionStore.put(action))
      specializationStore.delete(id)
      await transactionDone(transaction)
      return updated
    } finally { database.close() }
  }

  async listObjectives(areaId?: string): Promise<Objective[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(OBJECTIVE_STORE, 'readonly')
      const store = transaction.objectStore(OBJECTIVE_STORE)
      const request = areaId ? store.index('areaId').getAll(IDBKeyRange.only(areaId)) : store.getAll()
      const objectives = await requestResult(request as IDBRequest<Objective[]>)
      await transactionDone(transaction)
      return objectives.sort((a, b) => a.createdAt - b.createdAt)
    } finally { database.close() }
  }

  async createObjective(areaId: string, draft: ObjectiveDraft): Promise<void> {
    const name = checkedName(draft.name, 80, 'el Objetivo')
    const deadline = draft.deadline ?? null
    if (deadline !== null && (!isDeadline(deadline) || deadlineEnd(deadline) < Date.now())) throw new Error('Elige una fecha de entrega válida, desde hoy.')
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, OBJECTIVE_STORE], 'readwrite')
      const areaStore = transaction.objectStore(AREA_STORE)
      const objectiveStore = transaction.objectStore(OBJECTIVE_STORE)
      const [area, objectives] = await Promise.all([
        requestResult(areaStore.get(areaId) as IDBRequest<Area | undefined>),
        requestResult(objectiveStore.index('areaId').getAll(IDBKeyRange.only(areaId)) as IDBRequest<Objective[]>),
      ])
      if (!area) throw new Error('El Área ya no existe.')
      const shouldActivate = draft.activate || !objectives.some((item) => item.status === 'active')
      if (shouldActivate) objectives.filter((item) => item.status === 'active').forEach((item) => objectiveStore.put({ ...item, status: 'later', updatedAt: Date.now() }))
      const timestamp = Date.now()
      const objective: Objective = {
        id: crypto.randomUUID(), areaId, name, status: shouldActivate ? 'active' : 'later',
        priority: objectives.length, createdAt: timestamp, updatedAt: timestamp,
        deadline, originalDeadline: deadline, raceStartedAt: deadline ? timestamp : undefined, notes: [],
      }
      objectiveStore.add(objective)
      if (shouldActivate) areaStore.put({ ...area, activeObjectiveName: objective.name, updatedAt: timestamp })
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async updateObjective(id: string, draft: ObjectiveDraft): Promise<void> {
    const name = checkedName(draft.name, 80, 'el Objetivo')
    const deadline = draft.deadline ?? null
    if (deadline !== null && !isDeadline(deadline)) throw new Error('La fecha de entrega no es válida.')
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, OBJECTIVE_STORE], 'readwrite')
      const objectiveStore = transaction.objectStore(OBJECTIVE_STORE)
      const objective = await requestResult(objectiveStore.get(id) as IDBRequest<Objective | undefined>)
      if (!objective || objective.status === 'completed' || objective.status === 'archived') throw new Error('La Misión ya no está disponible para editar.')
      const timestamp = Date.now()
      if (deadline !== objective.deadline && deadline !== null && deadlineEnd(deadline) < timestamp) throw new Error('Elige una fecha de entrega desde hoy.')
      objectiveStore.put({ ...objective, name, deadline, originalDeadline: objective.originalDeadline ?? objective.deadline ?? deadline, raceStartedAt: objective.raceStartedAt ?? (deadline ? timestamp : undefined), updatedAt: timestamp })
      if (objective.status === 'active') {
        const areaStore = transaction.objectStore(AREA_STORE)
        const area = await requestResult(areaStore.get(objective.areaId) as IDBRequest<Area | undefined>)
        if (area) areaStore.put({ ...area, activeObjectiveName: name, updatedAt: timestamp })
      }
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async addMissionNote(id: string, proposedText: string, proposedNextStep: string, checkpoint: CheckpointAction = {}): Promise<void> {
    if (proposedText.trim().length > 2000) throw new Error('La nota admite hasta 2000 caracteres.')
    const text = checkedName(proposedText, 2000, 'la nota')
    const nextStep = proposedNextStep.trim()
    if (nextStep.length > 200) throw new Error('El siguiente paso admite hasta 200 caracteres.')
    const database = await openDatabase()
    try {
      const transaction = database.transaction([OBJECTIVE_STORE, ACTION_STORE], 'readwrite')
      const store = transaction.objectStore(OBJECTIVE_STORE)
      const actionStore = transaction.objectStore(ACTION_STORE)
      const objective = await requestResult(store.get(id) as IDBRequest<Objective | undefined>)
      if (!objective || (objective.status === 'completed' || objective.status === 'archived')) throw new Error('La Misión ya no está disponible para escribir.')
      if (checkpoint.actionId && checkpoint.newActionName) throw new Error('Elige una acción existente o crea una nueva.')
      const timestamp = Date.now()
      let actionId = checkpoint.actionId ?? null
      if (actionId) {
        const raw = await requestResult(actionStore.get(actionId) as IDBRequest<Action | undefined>)
        if (!raw || raw.areaId !== objective.areaId || raw.completedAt !== null) throw new Error('Elige una acción disponible de esta Área.')
        const action = normalizedAction(raw)
        actionStore.put({ ...action, objectiveIds: [...new Set([...action.objectiveIds, id])], updatedAt: timestamp })
      } else if (checkpoint.newActionName !== undefined) {
        const name = checkedName(checkpoint.newActionName, 80, 'la Acción')
        actionId = crypto.randomUUID()
        actionStore.add({ id: actionId, areaId: objective.areaId, name, type: 'simple', repeatMode: 'once',
          categoryIds: [], objectiveIds: [id], xp: 50, coins: 10, completionCount: 0, totalAmount: 0,
          completedAt: null, createdAt: timestamp, updatedAt: timestamp } satisfies Action)
      }
      store.put({ ...objective, notes: [...(objective.notes ?? []), { id: crypto.randomUUID(), text, nextStep, actionId, createdAt: timestamp }], updatedAt: timestamp })
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async activateObjective(id: string): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, OBJECTIVE_STORE], 'readwrite')
      const objectiveStore = transaction.objectStore(OBJECTIVE_STORE)
      const target = await requestResult(objectiveStore.get(id) as IDBRequest<Objective | undefined>)
      if (!target || (target.status === 'completed' || target.status === 'archived')) throw new Error('El Objetivo ya no está disponible.')
      const [area, objectives] = await Promise.all([
        requestResult(transaction.objectStore(AREA_STORE).get(target.areaId) as IDBRequest<Area | undefined>),
        requestResult(objectiveStore.index('areaId').getAll(IDBKeyRange.only(target.areaId)) as IDBRequest<Objective[]>),
      ])
      if (!area) throw new Error('El Área ya no existe.')
      const timestamp = Date.now()
      objectives.forEach((item) => {
        if (item.id === id) objectiveStore.put({ ...item, status: 'active', updatedAt: timestamp })
        else if (item.status === 'active') objectiveStore.put({ ...item, status: 'later', updatedAt: timestamp })
      })
      transaction.objectStore(AREA_STORE).put({ ...area, activeObjectiveName: target.name, updatedAt: timestamp })
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async completeObjective(id: string): Promise<void> { await this.finishObjective(id, 'completed') }

  async closeObjective(id: string): Promise<void> { await this.finishObjective(id, 'archived') }

  private async finishObjective(id: string, status: 'completed' | 'archived'): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, OBJECTIVE_STORE, MILESTONE_STORE, ACTION_STORE], 'readwrite')
      const store = transaction.objectStore(OBJECTIVE_STORE)
      const objective = await requestResult(store.get(id) as IDBRequest<Objective | undefined>)
      if (!objective || (objective.status !== 'active' && objective.status !== 'later')) throw new Error('La Misión ya está cerrada.')
      const area = await requestResult(transaction.objectStore(AREA_STORE).get(objective.areaId) as IDBRequest<Area | undefined>)
      if (!area) throw new Error('El Área ya no existe.')
      const timestamp = Date.now()
      const linked = (await requestResult(transaction.objectStore(ACTION_STORE).index('areaId').getAll(IDBKeyRange.only(objective.areaId)) as IDBRequest<Action[]>)).map(normalizedAction).filter((action) => action.objectiveIds.includes(id) && action.repeatMode === 'once')
      store.put({ ...objective, status, closedProgress: { completed: linked.filter((action) => action.completedAt !== null).length, total: linked.length }, updatedAt: timestamp })
      // Keep action links for the historical mission and for creating a fresh edition later.
      if (objective.status === 'active') transaction.objectStore(AREA_STORE).put({ ...area, activeObjectiveName: null, updatedAt: timestamp })
      if (status === 'completed') transaction.objectStore(MILESTONE_STORE).add({ id: crypto.randomUUID(), areaId: area.id, areaName: area.name, title: objective.name, kind: 'objective', xp: 0, coins: 0, achievedAt: timestamp } satisfies Milestone)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async repeatObjective(id: string): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, OBJECTIVE_STORE, ACTION_STORE], 'readwrite')
      const store = transaction.objectStore(OBJECTIVE_STORE)
      const source = await requestResult(store.get(id) as IDBRequest<Objective | undefined>)
      if (!source || (source.status !== 'completed' && source.status !== 'archived')) throw new Error('Primero completa o cierra la Misión.')
      const areaStore = transaction.objectStore(AREA_STORE)
      const actionStore = transaction.objectStore(ACTION_STORE)
      const [area, objectives, actions] = await Promise.all([
        requestResult(areaStore.get(source.areaId) as IDBRequest<Area | undefined>),
        requestResult(store.index('areaId').getAll(IDBKeyRange.only(source.areaId)) as IDBRequest<Objective[]>),
        requestResult(actionStore.index('areaId').getAll(IDBKeyRange.only(source.areaId)) as IDBRequest<Action[]>),
      ])
      if (!area) throw new Error('El Área ya no existe.')
      const timestamp = Date.now()
      const active = !objectives.some((item) => item.status === 'active')
      const objective: Objective = { id: crypto.randomUUID(), areaId: source.areaId, name: source.name, status: active ? 'active' : 'later', priority: objectives.length, createdAt: timestamp, updatedAt: timestamp, deadline: null, originalDeadline: null, notes: [] }
      store.add(objective)
      actions.map(normalizedAction).filter((action) => action.objectiveIds.includes(id)).forEach((action) => {
        if (action.repeatMode === 'once') {
          actionStore.add({ ...action, id: crypto.randomUUID(), objectiveIds: [objective.id], completionCount: 0, totalAmount: 0, completedAt: null, createdAt: timestamp, updatedAt: timestamp })
        } else {
          actionStore.put({ ...action, objectiveIds: [...new Set([...action.objectiveIds, objective.id])], updatedAt: timestamp })
        }
      })
      if (active) areaStore.put({ ...area, activeObjectiveName: objective.name, updatedAt: timestamp })
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async moveObjective(id: string, delta: -1 | 1): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(OBJECTIVE_STORE, 'readwrite')
      const store = transaction.objectStore(OBJECTIVE_STORE)
      const target = await requestResult(store.get(id) as IDBRequest<Objective | undefined>)
      if (!target || target.status !== 'later') throw new Error('El Objetivo ya no está en la lista Después.')
      const objectives = (await requestResult(store.index('areaId').getAll(IDBKeyRange.only(target.areaId)) as IDBRequest<Objective[]>)).filter((item) => item.status === 'later').sort((a, b) => a.priority - b.priority)
      const from = objectives.findIndex((item) => item.id === id)
      const to = Math.max(0, Math.min(objectives.length - 1, from + delta))
      if (from >= 0 && from !== to) {
        const [moved] = objectives.splice(from, 1)
        objectives.splice(to, 0, moved)
        const timestamp = Date.now()
        objectives.forEach((item, priority) => store.put({ ...item, priority, updatedAt: timestamp }))
      }
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async toggleActionObjective(objectiveId: string, actionId: string): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([ACTION_STORE, OBJECTIVE_STORE], 'readwrite')
      const actionStore = transaction.objectStore(ACTION_STORE)
      const [objective, rawAction] = await Promise.all([
        requestResult(transaction.objectStore(OBJECTIVE_STORE).get(objectiveId) as IDBRequest<Objective | undefined>),
        requestResult(actionStore.get(actionId) as IDBRequest<Action | undefined>),
      ])
      if (!objective || (objective.status === 'completed' || objective.status === 'archived')) throw new Error('El Objetivo ya no está disponible.')
      if (!rawAction || rawAction.completedAt !== null || rawAction.areaId !== objective.areaId) throw new Error('La Acción ya no está disponible.')
      const action = normalizedAction(rawAction)
      const linked = action.objectiveIds.includes(objectiveId)
      actionStore.put({ ...action, objectiveIds: linked ? action.objectiveIds.filter((id) => id !== objectiveId) : [...action.objectiveIds, objectiveId], updatedAt: Date.now() })
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async listActivityRecords(): Promise<ActivityRecord[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(ACTIVITY_STORE, 'readonly')
      const records = await requestResult(transaction.objectStore(ACTIVITY_STORE).getAll() as IDBRequest<ActivityRecord[]>)
      await transactionDone(transaction)
      return records.map((record) => ({ ...record, startedAt: typeof record.startedAt === 'number' ? record.startedAt : null })).sort((a, b) => b.completedAt - a.completedAt)
    } finally { database.close() }
  }

  async listMilestones(): Promise<Milestone[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(MILESTONE_STORE, 'readonly')
      const milestones = await requestResult(transaction.objectStore(MILESTONE_STORE).getAll() as IDBRequest<Milestone[]>)
      await transactionDone(transaction)
      return milestones.sort((a, b) => b.achievedAt - a.achievedAt)
    } finally { database.close() }
  }

  async listRewards(): Promise<RewardItem[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(REWARD_STORE, 'readonly')
      const rewards = await requestResult(transaction.objectStore(REWARD_STORE).getAll() as IDBRequest<RewardItem[]>)
      await transactionDone(transaction)
      return rewards.map(normalizedReward).sort((a, b) => a.createdAt - b.createdAt)
    } finally { database.close() }
  }

  async listRewardRedemptions(): Promise<RewardRedemption[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(REDEMPTION_STORE, 'readonly')
      const redemptions = await requestResult(transaction.objectStore(REDEMPTION_STORE).getAll() as IDBRequest<RewardRedemption[]>)
      await transactionDone(transaction)
      return redemptions.sort((a, b) => b.redeemedAt - a.redeemedAt)
    } finally { database.close() }
  }

  async getTargetRewardId(): Promise<string | null> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([REWARD_STORE, STATE_STORE], 'readonly')
      const stateStore = transaction.objectStore(STATE_STORE)
      const record = await requestResult(stateStore.get(TARGET_REWARD_KEY) as IDBRequest<StateRecord<string> | undefined>)
      const exists = record?.value ? await requestResult(transaction.objectStore(REWARD_STORE).getKey(record.value)) : undefined
      await transactionDone(transaction)
      return exists === undefined ? null : record!.value
    } finally { database.close() }
  }

  async setTargetRewardId(id: string | null): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([REWARD_STORE, STATE_STORE], 'readwrite')
      const stateStore = transaction.objectStore(STATE_STORE)
      if (id) {
        const exists = await requestResult(transaction.objectStore(REWARD_STORE).getKey(id))
        if (exists === undefined) throw new Error('La Recompensa ya no existe.')
        stateStore.put({ key: TARGET_REWARD_KEY, value: id } satisfies StateRecord<string>)
      } else stateStore.delete(TARGET_REWARD_KEY)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async createReward(proposedName: string, proposedCost: number): Promise<RewardItem> {
    const name = proposedName.trim().slice(0, 60)
    const cost = Math.trunc(proposedCost)
    if (!name) throw new Error('Escribe un nombre para la Recompensa.')
    if (!Number.isSafeInteger(cost) || cost < 1 || cost > 999_999_999) throw new Error('El costo debe ser de 1 a 999999999 monedas.')
    const timestamp = Date.now()
    const reward: RewardItem = { id: crypto.randomUUID(), name, cost, redeemedCount: 0, createdAt: timestamp, updatedAt: timestamp }
    const database = await openDatabase()
    try {
      const transaction = database.transaction(REWARD_STORE, 'readwrite')
      transaction.objectStore(REWARD_STORE).add(reward)
      await transactionDone(transaction)
      return reward
    } finally { database.close() }
  }

  async deleteReward(id: string): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([REWARD_STORE, STATE_STORE], 'readwrite')
      transaction.objectStore(REWARD_STORE).delete(id)
      const stateStore = transaction.objectStore(STATE_STORE)
      const target = await requestResult(stateStore.get(TARGET_REWARD_KEY) as IDBRequest<StateRecord<string> | undefined>)
      if (target?.value === id) stateStore.delete(TARGET_REWARD_KEY)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async redeemReward(id: string): Promise<RewardRedemptionResult> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([REWARD_STORE, REDEMPTION_STORE, STATE_STORE], 'readwrite')
      const rewardStore = transaction.objectStore(REWARD_STORE)
      const stateStore = transaction.objectStore(STATE_STORE)
      const [reward, coinRecord] = await Promise.all([
        requestResult(rewardStore.get(id) as IDBRequest<RewardItem | undefined>),
        requestResult(stateStore.get(COINS_KEY) as IDBRequest<StateRecord<number> | undefined>),
      ])
      if (!reward) throw new Error('La Recompensa ya no existe.')
      const currentCoins = Math.max(0, coinRecord?.value ?? 0)
      if (currentCoins < reward.cost) throw new Error('No tienes monedas suficientes para canjearla.')
      const redeemedAt = Date.now()
      const updated: RewardItem = { ...normalizedReward(reward), redeemedCount: clampTotal(reward.redeemedCount, 1), updatedAt: redeemedAt }
      const coins = currentCoins - reward.cost
      const redemption: RewardRedemption = { id: crypto.randomUUID(), rewardId: reward.id, rewardName: reward.name, cost: reward.cost, coinsAfter: coins, redeemedAt }
      rewardStore.put(updated)
      transaction.objectStore(REDEMPTION_STORE).add(redemption)
      stateStore.put({ key: COINS_KEY, value: coins } satisfies StateRecord<number>)
      await transactionDone(transaction)
      return { reward: updated, redemption, coins }
    } finally { database.close() }
  }

  async listActions(areaId?: string, includeCompleted = false): Promise<Action[]> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(ACTION_STORE, 'readonly')
      const store = transaction.objectStore(ACTION_STORE)
      const request = areaId ? store.index('areaId').getAll(IDBKeyRange.only(areaId)) : store.getAll()
      const actions = await requestResult(request as IDBRequest<Action[]>)
      await transactionDone(transaction)
      return actions.map(normalizedAction).filter((action) => includeCompleted || action.completedAt === null).sort((a, b) => a.createdAt - b.createdAt)
    } finally { database.close() }
  }

  async createAction(areaId: string, draft: ActionDraft): Promise<Action> {
    const name = checkedName(draft.name, 80, 'la Acción')
    const xp = checkedRewardValue(draft.xp, 1, 'El XP')
    const coins = checkedRewardValue(draft.coins, 0, 'Las monedas')
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, ACTION_STORE, SPECIALIZATION_STORE, OBJECTIVE_STORE], 'readwrite')
      const specializationStore = transaction.objectStore(SPECIALIZATION_STORE)
      const [area, specializations, objectives] = await Promise.all([
        requestResult(transaction.objectStore(AREA_STORE).get(areaId) as IDBRequest<Area | undefined>),
        requestResult(specializationStore.index('areaId').getAll(IDBKeyRange.only(areaId)) as IDBRequest<Specialization[]>),
        requestResult(transaction.objectStore(OBJECTIVE_STORE).index('areaId').getAll(IDBKeyRange.only(areaId)) as IDBRequest<Objective[]>),
      ])
      if (!area) throw new Error('El Área ya no existe.')
      const validIds = new Set(specializations.map((item) => item.id))
      const categoryIds = draft.categoryIds.filter((id) => validIds.has(id))
      const proposedName = draft.newSpecializationName?.trim().slice(0, 30)
      if (proposedName) {
        const existing = specializations.find((item) => item.name.toLocaleLowerCase() === proposedName.toLocaleLowerCase())
        if (existing) categoryIds.push(existing.id)
        else {
          const created: Specialization = { id: crypto.randomUUID(), areaId, name: proposedName, xp: 0, createdAt: Date.now() }
          specializationStore.add(created)
          categoryIds.push(created.id)
        }
      }
      const timestamp = Date.now()
      const validObjectiveIds = new Set(objectives.filter((item) => (item.status === 'active' || item.status === 'later')).map((item) => item.id))
      const action: Action = {
        id: crypto.randomUUID(), areaId, categoryIds: [...new Set(categoryIds)], objectiveIds: draft.objectiveIds.filter((id) => validObjectiveIds.has(id)), name, type: draft.type,
        repeatMode: draft.repeatMode, xp, coins,
        completionCount: 0, totalAmount: 0, completedAt: null, createdAt: timestamp, updatedAt: timestamp,
      }
      transaction.objectStore(ACTION_STORE).add(action)
      await transactionDone(transaction)
      return action
    } finally { database.close() }
  }

  async updateAction(id: string, draft: ActionDraft): Promise<Action> {
    const name = checkedName(draft.name, 80, 'la Acción')
    const xp = checkedRewardValue(draft.xp, 1, 'El XP')
    const coins = checkedRewardValue(draft.coins, 0, 'Las monedas')
    const database = await openDatabase()
    try {
      const transaction = database.transaction([ACTION_STORE, SPECIALIZATION_STORE], 'readwrite')
      const store = transaction.objectStore(ACTION_STORE)
      const raw = await requestResult(store.get(id) as IDBRequest<Action | undefined>)
      if (!raw || raw.completedAt !== null) throw new Error('La Acción ya no está disponible.')
      const current = normalizedAction(raw)
      const specializations = await requestResult(transaction.objectStore(SPECIALIZATION_STORE).index('areaId').getAll(IDBKeyRange.only(current.areaId)) as IDBRequest<Specialization[]>)
      const validIds = new Set(specializations.map((item) => item.id))
      const updated: Action = {
        ...current, name, type: current.type, repeatMode: draft.repeatMode,
        xp, coins,
        categoryIds: draft.categoryIds.filter((categoryId) => validIds.has(categoryId)), updatedAt: Date.now(),
      }
      store.put(updated)
      await transactionDone(transaction)
      return updated
    } finally { database.close() }
  }

  async deleteAction(id: string): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([ACTION_STORE, STATE_STORE], 'readwrite')
      const stateStore = transaction.objectStore(STATE_STORE)
      const activeRecord = await requestResult(stateStore.get(ACTIVE_ACTION_KEY) as IDBRequest<StateRecord<ActiveAction> | undefined>)
      transaction.objectStore(ACTION_STORE).delete(id)
      if (activeRecord?.value.actionId === id) stateStore.delete(ACTIVE_ACTION_KEY)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async getActiveAction(): Promise<ActiveAction | null> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(STATE_STORE, 'readonly')
      const record = await requestResult(transaction.objectStore(STATE_STORE).get(ACTIVE_ACTION_KEY) as IDBRequest<StateRecord<ActiveAction> | undefined>)
      await transactionDone(transaction)
      return record?.value ?? null
    } finally { database.close() }
  }

  async saveActiveAction(activeAction: ActiveAction | null): Promise<void> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(STATE_STORE, 'readwrite')
      const store = transaction.objectStore(STATE_STORE)
      if (activeAction) store.put({ key: ACTIVE_ACTION_KEY, value: activeAction } satisfies StateRecord<ActiveAction>)
      else store.delete(ACTIVE_ACTION_KEY)
      await transactionDone(transaction)
    } finally { database.close() }
  }

  async getCoins(): Promise<number> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction(STATE_STORE, 'readonly')
      const record = await requestResult(transaction.objectStore(STATE_STORE).get(COINS_KEY) as IDBRequest<StateRecord<number> | undefined>)
      await transactionDone(transaction)
      return Math.max(0, record?.value ?? 0)
    } finally { database.close() }
  }

  async completeAction(activeAction: ActiveAction): Promise<CompletionResult> {
    const database = await openDatabase()
    try {
      const transaction = database.transaction([AREA_STORE, ACTION_STORE, SPECIALIZATION_STORE, STATE_STORE, ACTIVITY_STORE, MILESTONE_STORE], 'readwrite')
      const areaStore = transaction.objectStore(AREA_STORE)
      const actionStore = transaction.objectStore(ACTION_STORE)
      const specializationStore = transaction.objectStore(SPECIALIZATION_STORE)
      const stateStore = transaction.objectStore(STATE_STORE)
      const activityStore = transaction.objectStore(ACTIVITY_STORE)
      const milestoneStore = transaction.objectStore(MILESTONE_STORE)
      const [area, rawAction, coinRecord, specializations] = await Promise.all([
        requestResult(areaStore.get(activeAction.areaId) as IDBRequest<Area | undefined>),
        requestResult(actionStore.get(activeAction.actionId) as IDBRequest<Action | undefined>),
        requestResult(stateStore.get(COINS_KEY) as IDBRequest<StateRecord<number> | undefined>),
        requestResult(specializationStore.index('areaId').getAll(IDBKeyRange.only(activeAction.areaId)) as IDBRequest<Specialization[]>),
      ])
      if (!area || !rawAction || rawAction.completedAt !== null || rawAction.areaId !== area.id) throw new Error('La Acción ya no está disponible.')
      const action = normalizedAction(rawAction)
      const amount = amountForCompletion(action, activeAction.amount)
      const reward = rewardFor(action, amount)
      const completedAt = Date.now()
      const updatedArea: Area = { ...area, xp: clampTotal(area.xp, reward.xp), updatedAt: completedAt }
      const updatedAction: Action = { ...action, completionCount: clampTotal(action.completionCount, 1), totalAmount: clampTotal(action.totalAmount, amount), completedAt: action.repeatMode === 'once' ? completedAt : null, updatedAt: completedAt }
      const updatedSpecializations = specializations.filter((item) => action.categoryIds.includes(item.id)).map((item) => ({ ...item, xp: clampTotal(item.xp, reward.xp) }))
      const updatedCoins = clampTotal(coinRecord?.value ?? 0, reward.coins)
      areaStore.put(updatedArea)
      actionStore.put(updatedAction)
      updatedSpecializations.forEach((item) => specializationStore.put(item))
      stateStore.put({ key: COINS_KEY, value: updatedCoins } satisfies StateRecord<number>)
      stateStore.delete(ACTIVE_ACTION_KEY)
      activityStore.add({
        id: crypto.randomUUID(),
        areaId: area.id,
        areaName: area.name,
        actionId: action.id,
        actionName: action.name,
        objectiveIds: [...action.objectiveIds],
        type: action.type,
        amount,
        xp: reward.xp,
        coins: reward.coins,
        startedAt: activeAction.startedAt,
        completedAt,
      } satisfies ActivityRecord)
      if (action.repeatMode === 'once') {
        milestoneStore.add({
          id: crypto.randomUUID(),
          areaId: area.id,
          areaName: area.name,
          title: action.name,
          kind: 'uniqueAction',
          xp: reward.xp,
          coins: reward.coins,
          achievedAt: completedAt,
        } satisfies Milestone)
      }
      await transactionDone(transaction)
      return { area: updatedArea, action: updatedAction, specializations: updatedSpecializations, coins: updatedCoins, gainedXp: reward.xp, gainedCoins: reward.coins }
    } finally { database.close() }
  }
}
